<template>
    <div class="pdf-container" ref="pdfContainer">
        <PdfToolbar
            :title="title"
            :current-page="currentPage"
            :total-pages="pages"
            :is-loading="isLoading"
            :error="error"
            :scale="scale"
            :fit-mode="fitMode"
            :disable-download-button="disableDownloadButton"
            :printer-loading="printerLoading"
            :downloading="downloading"
            @page-change="handlePageChange"
            @scale-change="handleScaleChange"
            @fit-change="handleFitChange"
            @rotate-page="handleRotatePage"
            @download-pdf="handleDownloadPdf"
            @print-pdf="handlePrintPdf"
        />

        <PdfCanvas
            v-if="!isLoading && !error"
            ref="pdfCanvas"
            :pdf="pdf"
            :pages="pages"
            :pdf-width="pdfWidth"
            :pdf-height="pdfHeight"
            :current-page="currentPage"
            :rotate="rotate"
            :fit-mode="fitMode"
            :scale="scale"
            :max-rendered-pages="maxRenderedPages"
            @page-changed="updateCurrentPage"
            @error="handlePdfError"
        />

        <!-- Loading state -->
        <div v-if="isLoading" class="pdf-loading">
            <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>

        <!-- Error state -->
        <div v-if="error" class="pdf-error">
            <p>{{ error }}</p>
            <button @click="loadPdf" class="btn btn-primary">Retry</button>
        </div>
    </div>
</template>

<script>
import PdfToolbar from './PdfToolbar.vue';
import PdfCanvas from './PdfCanvas.vue';

export default {
    name: 'PdfComponent',
    components: {
        PdfToolbar,
        PdfCanvas,
    },
    props: {
        hash: {
            type: String,
            required: true,
        },
        initialPage: {
            type: Number,
            default: 1,
        },
    },
    data() {
        return {
            pdf: null,
            pages: null,
            documentMetadata: null,
            title: '',
            currentPage: 1,
            rotate: 0,
            isLoading: false,
            error: null,
            currentBlobUrl: null,

            // Scaling
            scale: 1.0,
            fitMode: null,

            // Scale constants
            SCALE_STEP: 0.25,
            MIN_SCALE: 0.1,
            MAX_SCALE: 5.0,

            // Fallback dimensions (A4 at 72 DPI)
            STANDARD_PAGE_WIDTH: 595,
            STANDARD_PAGE_HEIGHT: 842,

            // Actual PDF page dimensions (set when PDF loads)
            actualPageWidth: 0,
            actualPageHeight: 0,

            // Container dimensions for fit calculations
            containerWidth: 0,
            containerHeight: 0,

            // print
            printerLoading: false,
            downloading: false,
        };
    },
    computed: {
        isRotated() {
            return this.rotate === 90 || this.rotate === 270;
        },

        pdfWidth() {
            const baseWidth = this.isRotated
                ? this.actualPageHeight || this.STANDARD_PAGE_HEIGHT
                : this.actualPageWidth || this.STANDARD_PAGE_WIDTH;

            return Math.floor(this.scale * baseWidth);
        },

        pdfHeight() {
            const baseHeight = this.isRotated
                ? this.actualPageWidth || this.STANDARD_PAGE_WIDTH
                : this.actualPageHeight || this.STANDARD_PAGE_HEIGHT;

            return Math.floor(this.scale * baseHeight);
        },

        // Calculate available container space for fit modes
        availableWidth() {
            return Math.max(
                0,
                this.containerWidth -
                    2 * this.getCssVarNumber('--page-margin-inline', 0.25)
            );
        },

        availableHeight() {
            return Math.max(
                0,
                this.containerHeight -
                    (this.getCssVarNumber('--page-gap-bottom', 10) +
                        this.getCssVarNumber(
                            '--scroll-container-padding-top',
                            4
                        ))
            );
        },

        disableDownloadButton() {
            const documentAppAdminOnly =
                this.documentMetadata?.company_id == 0 ||
                this.documentMetadata?.company_id == null ||
                this.documentMetadata?.app_admin_only;
            if (documentAppAdminOnly) {
                const isAppAdmin = this.$userStore.isAppAdmin;
                return !isAppAdmin;
            }
            return false;
        },

        maxRenderedPages() {
            //if the available height is greater than combined 6 pages height, render pages based on available height
            if (this.availableHeight > this.pdfHeight * 6) {
                const pages = Math.floor(this.availableHeight / this.pdfHeight);
                return pages * 2;
            }
            return 6; // default to 6 pages
        },
    },
    watch: {
        hash: {
            handler(newHash) {
                if (newHash) {
                    this.loadPdf();
                }
            },
            immediate: true,
        },
        pdf: {
            handler(newPdf) {
                if (newPdf) {
                    this.isLoading = false;
                    this.pages = this.pdf.numPages;
                    this.$nextTick(() => {
                        this.updateContainerDimensions();
                        this.getActualPageDimensions();
                        // Navigate to initial page if specified
                        if (this.initialPage && this.initialPage !== 1) {
                            this.$nextTick(() => {
                                this.handlePageChange(this.initialPage);
                            });
                        }
                    });
                }
            },
        },
        // Auto-update scale when container dimensions change in fit modes
        availableWidth() {
            if (this.fitMode === 'width') {
                this.updateScaleForCurrentFitMode();
            }
        },
        availableHeight() {
            if (this.fitMode === 'height') {
                this.updateScaleForCurrentFitMode();
            }
        },
        // Handle rotation changes
        rotate() {
            this.updateScaleForCurrentFitMode();
        },
    },
    methods: {
        initializeResizeObserver() {
            if (
                typeof ResizeObserver !== 'undefined' &&
                this.$refs.pdfContainer
            ) {
                this.resizeObserver = new ResizeObserver(() => {
                    this.updateContainerDimensions();
                });
                this.resizeObserver.observe(this.$refs.pdfContainer);
            }
        },

        updateContainerDimensions() {
            if (this.$refs.pdfContainer) {
                const rect = this.$refs.pdfContainer.getBoundingClientRect();
                this.containerWidth = rect.width;
                this.containerHeight =
                    rect.height -
                    this.getCssVarNumber('--pdf-toolbar-height', 60); // Subtract toolbar height
            }
        },

        async getActualPageDimensions() {
            if (!this.pdf) return;

            try {
                const page = await this.pdf.getPage(1);
                const viewport = page.getViewport({ scale: 1.0 });

                // page actual dimensions
                this.actualPageWidth = viewport.width;
                this.actualPageHeight = viewport.height;

                this.fitPageOnLoad();
            } catch (error) {
                console.error('Error getting page dimensions:', error);
                this.actualPageWidth = this.STANDARD_PAGE_WIDTH;
                this.actualPageHeight = this.STANDARD_PAGE_HEIGHT;
            }
        },

        fitPageOnLoad() {
            if (this.fitMode) {
                this.updateScaleForCurrentFitMode();
                return;
            }

            const targetWidth = this.isRotated
                ? this.actualPageHeight || this.STANDARD_PAGE_HEIGHT
                : this.actualPageWidth || this.STANDARD_PAGE_WIDTH;

            if (!targetWidth || !this.availableWidth) return;

            const currentWidth = this.scale * targetWidth;

            // If the page would overflow horizontally, enable fit-to-width
            if (currentWidth > this.availableWidth) {
                this.scale = this.clampScale(this.availableWidth / targetWidth);
            }
        },

        updateScaleForCurrentFitMode() {
            if (this.fitMode === 'width') {
                this.updateScaleForFitWidth();
            } else if (this.fitMode === 'height') {
                this.updateScaleForFitHeight();
            }
        },

        updateScaleForFitWidth() {
            if (this.actualPageWidth > 0 && this.availableWidth > 0) {
                const targetWidth = this.isRotated
                    ? this.actualPageHeight
                    : this.actualPageWidth;

                const newScale = this.availableWidth / targetWidth;
                this.scale = this.clampScale(newScale);
            }
        },

        updateScaleForFitHeight() {
            if (this.actualPageHeight > 0 && this.availableHeight > 0) {
                const targetHeight = this.isRotated
                    ? this.actualPageWidth
                    : this.actualPageHeight;

                const newScale = this.availableHeight / targetHeight;
                this.scale = this.clampScale(newScale);
            }
        },

        clampScale(scale) {
            return Math.min(this.MAX_SCALE, Math.max(this.MIN_SCALE, scale));
        },

        getCssVarNumber(name, fallback) {
            try {
                const el = this.$refs.pdfContainer || this.$el;
                const raw = getComputedStyle(el).getPropertyValue(name).trim();
                if (!raw) return fallback;
                if (raw.endsWith('rem')) return parseFloat(raw) * 16;
                if (raw.endsWith('px')) return parseFloat(raw);
                return fallback;
            } catch {
                return fallback;
            }
        },

        async loadPdf() {
            if (!this.hash) {
                this.error = 'No PDF hash provided';
                return;
            }

            this.cleanup();
            this.isLoading = true;
            this.error = null;
            this.title = 'Loading PDF...';
            this.scale = 1.0;
            this.fitMode = null;

            try {
                await this.getFileMetadata();

                const [blob] = await this.$getFileBlob({
                    hash: this.hash,
                });

                if (blob && blob.type === 'application/pdf') {
                    this.currentBlobUrl = URL.createObjectURL(blob);
                    const { useVuePdfEmbed } = await import('vue-pdf-embed');
                    const { doc } = useVuePdfEmbed({
                        source: this.currentBlobUrl,
                    });
                    this.pdf = doc;
                } else {
                    throw new Error('The fetched file is not a valid PDF');
                }
            } catch (err) {
                console.error('Error loading PDF:', err);
                this.error = err.message || 'Failed to load PDF file';
                this.isLoading = false;
            }
        },

        async getFileMetadata() {
            const data = await this.$getData('upload/details', {
                where: {
                    hash: this.hash,
                },
            });

            if (data?.success && data?.data?.length > 0) {
                this.documentMetadata = data.data[0];
                this.title = data.data[0].original_filename;
            } else {
                this.title = 'PDF Document';
            }
        },

        handlePageChange(pageNumber) {
            if (pageNumber >= 1 && pageNumber <= this.pages) {
                this.currentPage = pageNumber;
                this.$refs.pdfCanvas?.scrollToPage(pageNumber);
            }
        },

        handleScaleChange(action) {
            this.fitMode = null; // Exit fit mode when manually changing scale

            if (action === 'increase') {
                this.scale = this.clampScale(this.scale + this.SCALE_STEP);
            } else if (action === 'decrease') {
                this.scale = this.clampScale(this.scale - this.SCALE_STEP);
            } else if (typeof action === 'number') {
                this.scale = this.clampScale(action);
            }
        },

        handleFitChange(mode) {
            if (mode === 'width') {
                this.fitMode = this.fitMode === 'width' ? null : 'width';
                if (this.fitMode === 'width') {
                    this.updateScaleForFitWidth();
                }
            } else if (mode === 'height') {
                this.fitMode = this.fitMode === 'height' ? null : 'height';
                if (this.fitMode === 'height') {
                    this.updateScaleForFitHeight();
                }
            } else {
                this.fitMode = null;
            }
        },

        handleRotatePage() {
            this.rotate = (this.rotate + 90) % 360;
        },

        handleDownloadPdf() {
            this.downloading = true;
            this.$refs.pdfCanvas?.downloadPdf(this.title).then(() => {
                this.downloading = false;
            });
        },

        handlePrintPdf() {
            this.printerLoading = true;
            this.$refs.pdfCanvas?.printPdf(this.title).then(() => {
                this.printerLoading = false;
            });
        },

        updateCurrentPage(pageNumber) {
            if (pageNumber !== this.currentPage) {
                this.currentPage = pageNumber;
            }
        },

        handleGlobalPrintShortcut(event) {
            // Detect Ctrl+P (Windows/Linux) or Cmd+P (macOS)
            const isPKey = event.key && event.key.toLowerCase() === 'p';
            const hasPrintModifier = event.ctrlKey || event.metaKey;
            const hasOtherModifiers = event.shiftKey || event.altKey;
            if (!isPKey || !hasPrintModifier || hasOtherModifiers) {
                return;
            }
            event.preventDefault();
            if (this.isLoading || this.error) return;
            if (this.disableDownloadButton) {
                this.$alertStore.pushError(
                    'You do not have permission to print this PDF.'
                );
                return;
            }
            if (this.printerLoading) return;

            this.handlePrintPdf();
        },

        handlePdfError(error) {
            this.error = error;
            console.warn('PDF error:', error);
        },

        cleanup() {
            if (this.currentBlobUrl) {
                URL.revokeObjectURL(this.currentBlobUrl);
                this.currentBlobUrl = null;
            }
            this.pdf = null;
            this.pages = null;
            this.actualPageWidth = 0;
            this.actualPageHeight = 0;
            this.scale = 1.0;
            this.fitMode = null;
        },
    },
    mounted() {
        this.initializeResizeObserver();
        this.updateContainerDimensions();
        // Listen for global print shortcuts
        window.addEventListener('keydown', this.handleGlobalPrintShortcut);
    },
    beforeUnmount() {
        this.cleanup();
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        window.removeEventListener('keydown', this.handleGlobalPrintShortcut);
    },
};
</script>

<style scoped>
.pdf-container {
    --pdf-toolbar-height: 3.75rem;

    --scroll-container-padding-top: var(--space-1);
    --page-gap-bottom: 0.625rem;
    --page-gap-top: 0.625rem;
    --page-margin-inline: var(--space-1);

    position: relative;
    background-color: #d4d4d7;
    width: 100%;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.pdf-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 2rem;
}

.pdf-loading .spinner-border {
    width: 3rem;
    height: 3rem;
}


.pdf-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 2rem;
    color: var(--danger);
}
</style>
